import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Wrench, AlertCircle, Search, Clock, Calendar, Server, 
  PlayCircle, PauseCircle, TimerOff, ChevronRight, Users, FolderTree,
  CalendarClock, Repeat, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const config = {
    active: { label: 'Em Manutenção', className: 'bg-orange-500 text-white', icon: Wrench },
    scheduled: { label: 'Agendada', className: 'bg-blue-500 text-white', icon: CalendarClock },
    expired: { label: 'Expirada', className: 'bg-gray-500 text-white', icon: TimerOff },
  };
  
  const { label, className, icon: Icon } = config[status] || config.expired;
  
  return (
    <Badge className={`${className} text-xs flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function TypeBadge({ type }) {
  if (type === 0) {
    return (
      <Badge variant="outline" className="text-xs">
        <PlayCircle className="h-3 w-3 mr-1" />
        Com coleta
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs">
      <PauseCircle className="h-3 w-3 mr-1" />
      Sem coleta
    </Badge>
  );
}

function StatsCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MaintenanceCard({ maintenance, onViewDetails }) {
  return (
    <Card className={`
      ${maintenance.status === 'active' ? 'border-orange-500 border-2' : ''}
      ${maintenance.status === 'scheduled' ? 'border-blue-500 border' : ''}
      hover:shadow-md transition-shadow cursor-pointer
    `} onClick={() => onViewDetails(maintenance)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="h-4 w-4" />
              {maintenance.name}
            </CardTitle>
            {maintenance.description && (
              <CardDescription className="text-xs line-clamp-2">
                {maintenance.description}
              </CardDescription>
            )}
          </div>
          <StatusBadge status={maintenance.status} />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Period */}
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              {maintenance.active_since_str} → {maintenance.active_till_str}
            </span>
          </div>
          
          {/* Duration */}
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>Duração: <strong>{maintenance.duration}</strong></span>
          </div>
          
          {/* Type */}
          <div className="flex items-center gap-2">
            <TypeBadge type={maintenance.maintenance_type} />
          </div>
          
          {/* Hosts and Groups */}
          <div className="flex items-center gap-4 pt-2 border-t">
            <div className="flex items-center gap-1 text-sm">
              <Server className="h-4 w-4 text-muted-foreground" />
              <span><strong>{maintenance.host_count}</strong> hosts</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              <FolderTree className="h-4 w-4 text-muted-foreground" />
              <span><strong>{maintenance.group_count}</strong> grupos</span>
            </div>
          </div>
          
          {/* Host names preview */}
          {maintenance.host_names?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {maintenance.host_names.slice(0, 3).map((name) => (
                <Badge key={name} variant="outline" className="text-[10px]">
                  {name}
                </Badge>
              ))}
              {maintenance.host_names.length > 3 && (
                <Badge variant="secondary" className="text-[10px]">
                  +{maintenance.host_names.length - 3} mais
                </Badge>
              )}
            </div>
          )}
        </div>
        
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" className="text-xs">
            Ver Detalhes <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MaintenanceDetailDialog({ maintenance, open, onClose }) {
  if (!maintenance) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            {maintenance.name}
          </DialogTitle>
          <DialogDescription>
            {maintenance.description || 'Sem descrição'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Status and Type */}
          <div className="flex items-center gap-4">
            <StatusBadge status={maintenance.status} />
            <TypeBadge type={maintenance.maintenance_type} />
          </div>
          
          {/* Period Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Período da Manutenção
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Início</p>
                  <p className="font-medium">{maintenance.active_since_str}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fim</p>
                  <p className="font-medium">{maintenance.active_till_str}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Duração Total</p>
                  <p className="font-medium text-lg">{maintenance.duration}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Time Periods */}
          {maintenance.timeperiods?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Repeat className="h-4 w-4" />
                  Períodos de Tempo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {maintenance.timeperiods.map((tp, i) => (
                    <div key={i} className="p-2 bg-muted rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{tp.type_label}</Badge>
                        <span>Duração: {tp.period_str}</span>
                      </div>
                      {tp.start_date && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Início: {tp.start_date}
                        </p>
                      )}
                      {tp.days_of_week?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Dias: {tp.days_of_week.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Hosts */}
          {maintenance.host_names?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Server className="h-4 w-4" />
                  Hosts Afetados ({maintenance.host_count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {maintenance.host_names.map((name) => (
                    <Badge key={name} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Groups */}
          {maintenance.group_names?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FolderTree className="h-4 w-4" />
                  Grupos Afetados ({maintenance.group_count})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {maintenance.group_names.map((name) => (
                    <Badge key={name} variant="outline" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MaintenancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/maintenance`, { 
        headers: getAuthHeader() 
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar manutenções');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
    // Refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleViewDetails = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setDetailDialogOpen(true);
  };

  // Filter maintenances
  const filterMaintenances = (maintenances, status) => {
    return maintenances?.filter(m => {
      // Status filter
      if (status !== 'all' && m.status !== status) return false;
      
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch = 
          m.name.toLowerCase().includes(term) ||
          m.description?.toLowerCase().includes(term) ||
          m.host_names?.some(h => h.toLowerCase().includes(term)) ||
          m.group_names?.some(g => g.toLowerCase().includes(term));
        if (!matchesSearch) return false;
      }
      
      return true;
    }) || [];
  };

  const activeMaintenances = filterMaintenances(data?.maintenances, 'active');
  const scheduledMaintenances = filterMaintenances(data?.maintenances, 'scheduled');
  const expiredMaintenances = filterMaintenances(data?.maintenances, 'expired');
  const allMaintenances = filterMaintenances(data?.maintenances, 'all');

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard 
          title="Total de Manutenções" 
          value={data?.total || 0} 
          icon={Wrench} 
          color="bg-gray-500"
        />
        <StatsCard 
          title="Em Manutenção" 
          value={data?.active || 0}
          subtitle={`${data?.hosts_in_maintenance || 0} hosts afetados`}
          icon={Wrench} 
          color="bg-orange-500"
        />
        <StatsCard 
          title="Agendadas" 
          value={data?.scheduled || 0} 
          subtitle="aguardando início"
          icon={CalendarClock} 
          color="bg-blue-500"
        />
        <StatsCard 
          title="Expiradas" 
          value={data?.expired || 0}
          subtitle="finalizadas"
          icon={TimerOff} 
          color="bg-gray-400"
        />
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar manutenções, hosts ou grupos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-maintenance"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Em Manutenção
            {activeMaintenances.length > 0 && (
              <Badge className="bg-orange-500 text-white ml-1">{activeMaintenances.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="scheduled" className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4" />
            Agendadas
            {scheduledMaintenances.length > 0 && (
              <Badge variant="secondary" className="ml-1">{scheduledMaintenances.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="expired" className="flex items-center gap-2">
            <TimerOff className="h-4 w-4" />
            Expiradas
          </TabsTrigger>
          <TabsTrigger value="all">Todas</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {activeMaintenances.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeMaintenances.map(m => (
                <MaintenanceCard key={m.id} maintenance={m} onViewDetails={handleViewDetails} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Wrench className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Nenhum host em manutenção no momento</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="scheduled" className="mt-4">
          {scheduledMaintenances.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scheduledMaintenances.map(m => (
                <MaintenanceCard key={m.id} maintenance={m} onViewDetails={handleViewDetails} />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <CalendarClock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Nenhuma manutenção agendada</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="expired" className="mt-4">
          {expiredMaintenances.length > 0 ? (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Hosts</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiredMaintenances.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {m.active_since_str} → {m.active_till_str}
                      </TableCell>
                      <TableCell>{m.duration}</TableCell>
                      <TableCell>{m.host_count} hosts</TableCell>
                      <TableCell>
                        <TypeBadge type={m.maintenance_type} />
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(m)}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <TimerOff className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Nenhuma manutenção expirada</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          {allMaintenances.length > 0 ? (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Hosts</TableHead>
                    <TableHead>Grupos</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allMaintenances.map(m => (
                    <TableRow 
                      key={m.id}
                      className={m.status === 'active' ? 'bg-orange-500/5' : ''}
                    >
                      <TableCell>
                        <StatusBadge status={m.status} />
                      </TableCell>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex flex-col">
                          <span>{m.active_since_str}</span>
                          <span>{m.active_till_str}</span>
                        </div>
                      </TableCell>
                      <TableCell>{m.duration}</TableCell>
                      <TableCell>{m.host_count}</TableCell>
                      <TableCell>{m.group_count}</TableCell>
                      <TableCell>
                        <TypeBadge type={m.maintenance_type} />
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetails(m)}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <Wrench className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>Nenhuma manutenção cadastrada</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <MaintenanceDetailDialog 
        maintenance={selectedMaintenance}
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
      />
    </div>
  );
}
